/**
 * A short, rolling record of what went wrong in the browser.
 *
 * This exists because of the gap between the two people involved in a bug. The
 * accounting user knows what they were doing and nothing about the error; the
 * console knows the error and nothing about what they were doing. A report that
 * carries both is the difference between "the review screen broke" and a stack
 * trace with a route and a timestamp beside it.
 *
 * **Module state, deliberately, not a React context.** The first thing worth
 * capturing is an error thrown while the app is still booting, before any
 * provider exists - and a buffer that only starts recording once React has
 * mounted would miss precisely the failures nobody can otherwise explain.
 *
 * It is memory only. A reload empties it, which is the same bargain
 * `RunProvider` makes and for the same reason: persisting this would mean
 * writing error text - which can quote data - into browser storage.
 */
import type { ClientLogEntry, ClientLogLevel } from "@/shared/api";

/** Matches `MAX_LOG_ENTRIES` in `backend/app/domain/feedback.py`. */
const CAPACITY = 50;

/** Matches the per-message cap the API enforces on `ClientLogEntry.message`. */
const MAX_MESSAGE = 500;

/**
 * Oldest first, which is the order a reader reconstructing a sequence wants.
 * Capped at {@link CAPACITY}: a page left open all afternoon must not grow a
 * buffer, and the entries just before a report are the ones that matter.
 */
const entries: ClientLogEntry[] = [];

/**
 * Records one line.
 *
 * Never throws. It is called from an error handler and from a wrapped
 * `console.error`, so a failure in here would either swallow the original
 * problem or recurse - both worse than losing a log line.
 *
 * @param level - How bad the caller thought it was.
 * @param message - What to record. Truncated to the API's cap.
 */
export function recordLog(level: ClientLogLevel, message: string): void {
  try {
    const trimmed =
      message.length > MAX_MESSAGE ? `${message.slice(0, MAX_MESSAGE - 1)}…` : message;

    entries.push({ at: new Date().toISOString(), level, message: trimmed });

    if (entries.length > CAPACITY) entries.splice(0, entries.length - CAPACITY);
  } catch {
    // Nothing useful to do about it, and nowhere safe to say so.
  }
}

/**
 * Returns a copy of what has been recorded, oldest first.
 *
 * A copy rather than the array itself, so a caller holding it while more
 * arrives does not watch its own snapshot change underneath it.
 */
export function logSnapshot(): ClientLogEntry[] {
  return [...entries];
}

/** Empties the buffer. For tests, and for after a report is filed. */
export function clearLog(): void {
  entries.length = 0;
}
